import { Buffer } from "buffer";

import React, { useState } from "react";

import { Box, CircularProgress } from "@mui/material";
import ScreenshotMonitorIcon from "@mui/icons-material/ScreenshotMonitor";
import { useMutation } from "@tanstack/react-query";
import cryptoRandomString from "crypto-random-string";
import { useTranslation } from "react-i18next";

import amazonS3 from "../../../../amazonS3";
import { Screenshot } from "../../../../models/screenshot";
import ScreenshotService from "../../../../services/screenshot.service";
import { _arrayBufferToBase64 } from "../../../../util/base64";
import { getTotalTime } from "../../../../util/getTotalTime";
import { NotificationToast } from "../../../Generic/Toast/NotificationToast";
import useParticipants from "../../hooks/useParticipants/useParticipants";
import useVideoContext from "../../hooks/useVideoContext/useVideoContext";
import { ActionIconButton } from "../ActionIconButton";

export default function ScreenshotButton(props: {
  disabled?: boolean;
  hours: number;
  minutes: number;
  seconds: number;
}) {
  const { t: translate } = useTranslation();
  const { hours, minutes, seconds } = props;

  const { room, task, taskIsValid, participantsAllowed, setScreenshots } = useVideoContext();

  const participants = useParticipants();

  const [disabled, setDisabled] = useState<boolean>(false);
  const [isLoadingScreenshot, setIsLoadingScreenshot] = useState<boolean>(false);

  const { mutate: createScreenshot, isLoading } = useMutation(
    ["video.survey.screenshot.save"],
    (screenshot: Screenshot) => ScreenshotService.create(screenshot),
    {
      onSuccess: () => {
        NotificationToast.success(translate("screenshotSavedSuccessfully"));
        setIsLoadingScreenshot(false);
      },
      onError: () => {
        setScreenshots((state) => state.splice(-1));
        NotificationToast.error(translate("couldNotSaveScreenshot"));
        setIsLoadingScreenshot(false);
      },
    }
  );

  const createCanvas = (video: any) => {
    const w = video.videoWidth;
    const h = video.videoHeight;

    const canvas = document.createElement("canvas");

    canvas.width = w;
    canvas.height = h;

    const context = canvas.getContext("2d");

    context?.drawImage(video, 0, 0, w, h);

    return canvas;
  };

  const onTakeScreenshot = async () => {
    setDisabled(true);
    setIsLoadingScreenshot(true);

    try {
      if (!taskIsValid() || !participantsAllowed || participantsAllowed.length === 0) {
        NotificationToast.error(translate("noParticipantsAvailable"));
        return;
      }

      const arrayBuffer: ArrayBuffer[] = [];

      for (const participant of participants) {
        const hasAllowed = participantsAllowed.find(
          (identity) => identity === participant.identity
        );

        if (hasAllowed) {
          const video = document.getElementById(participant.sid);

          if (video) {
            const canvas = createCanvas(video);

            canvas.onclick = () => {
              window.open(canvas.toDataURL("image/jpg"));
            };

            const buffer: ArrayBuffer | undefined = await new Promise(async (resolve) => {
              canvas.toBlob(
                async (blob) => {
                  const buffer = await blob?.arrayBuffer();

                  resolve(buffer);
                },
                "image/png",
                1
              );
            });

            if (buffer) arrayBuffer.push(buffer);
          }
        }
      }

      if (arrayBuffer.length === 0) {
        NotificationToast.error(translate("noScreenshotsCaptured"));
        return;
      }

      for (const buffer of arrayBuffer) {
        try {
          setScreenshots((state) => [
            ...state,
            { link: `data:image/png;base64,${_arrayBufferToBase64(buffer)}` },
          ]);

          const link = await amazonS3.uploadPhoto(
            `screenshots-${cryptoRandomString({
              length: 30,
              type: "alphanumeric",
            })}`,
            Buffer.from(buffer)
          );

          if (!link) {
            NotificationToast.error(translate("couldNotSaveScreenshot"));
            throw new Error("Failed to upload screenshot");
          }

          setDisabled(false);
          createScreenshot({
            videoTime: getTotalTime(hours, minutes, seconds),
            taskId: task?.id,
            link,
          });
        } catch (error) {
          console.error("Screenshot upload error:", error);
          NotificationToast.error(translate("couldNotSaveScreenshot"));
          throw error;
        }
      }
    } catch (error) {
      console.error("Screenshot process error:", error);
      NotificationToast.error(translate("couldNotSaveScreenshot"));
    } finally {
      setDisabled(false);
      setIsLoadingScreenshot(false);
    }
  };

  const renderButtonIcon = () => {
    if (isLoadingScreenshot) {
      return (
        <Box width="100%" display="flex" flexDirection="column" alignItems="center">
          <CircularProgress size={22} />
        </Box>
      );
    }

    return <ScreenshotMonitorIcon />;
  };

  return (
    <ActionIconButton
      dataCy="screenshotAction"
      onClick={onTakeScreenshot}
      disabled={!room || participantsAllowed?.length === 0 || isLoading || disabled}
      tooltipTitle={translate("takeAScreenshot")}
    >
      {renderButtonIcon()}
    </ActionIconButton>
  );
}
